import type * as models from 'pkg/api/models';
import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { Group } from 'pkg/api/models/group';
import type { Membership } from 'pkg/api/models/membership';
import type { Record } from 'pkg/api/models/record';
import type { Scrubbable } from 'pkg/api/models/scrubbable';
import type { UserAddress } from 'pkg/api/models/user_address';
import type {
	UserField,
	UserFieldType,
	Visibility,
} from 'pkg/api/models/user_fields';
import type { UserProduct } from 'pkg/api/models/user_product';

export enum UserSex {
	Unknown = 0,
	Male = 1,
	Female = 2,
	NotApplicable = 9,
}

export enum Type {
	Normal = 'normal',
	Bot = 'bot',
	TSAdmin = 'tsAdmin',
}

export interface Field {
	userFieldId: number;
	label: string;
	key: string;
	visibility: Visibility[];
	values?: string[];
	attachments?: models.attachment.Attachment[];
	type: UserFieldType;
}

export enum MetaField {
	Height = 'height',
	MotherHeight = 'mother-height',
	FatherHeight = 'father-height',
	RelativeOneName = 'relative-one-name',
	RelativeOneNumber = 'relative-one-number',
	RelativeOneEmail = 'relative-one-email',
	RelativeTwoName = 'relative-two-name',
	RelativeTwoNumber = 'relative-two-number',
	RelativeTwoEmail = 'relative-two-email',
	Allergies = 'allergies',
	Misc = 'misc',
	LegalGuardian = 'legal-guardian-for',
}

export interface IdentityData extends Dateable, Record {
	identityProviderId: number;
	userId: number;
}

interface UserMetaData extends Record {
	key: string;
	value: string;
}

export interface User extends Scrubbable, Record, Dateable, AllowedActionable {
	accountId?: number;
	organizationId: number;
	billingUserId?: number;
	firstName: string;
	lastName?: string;
	companyName?: string;
	email?: string;
	sex?: UserSex;
	address?: string;
	region?: string;
	postalCode?: string;
	city?: string;
	country: string;
	phoneNumber?: string;
	birthDate?: string;
	profileImageUrl?: string;
	deletedAt?: number;
	meta?: UserMetaData[];
	flags?: string[];
	account?: any;
	paymentProviderId?: any;
	taxIdType?: string;
	taxIdValue?: string;
	language?: string;
	languageCode?: string;
	nationality?: string;
	lokSwedishPersonalId?: string;
	lokValid?: boolean;
	lokError?: string;
	type?: Type;

	identityData?: IdentityData[];

	groups?: Membership[];
	addresses?: UserAddress[];
	billingUser?: User;
	products?: UserProduct[];
	organization?: Group;
	fields?: Field[];
	missingRequiredFields?: UserField[];
	wards?: User[];
}

export interface LoginInformation {
	accountEmail: string;
	usesSignInWithApple: boolean;
	hasPassword: boolean;
	hasEmailCode: boolean;
	hasAppleAuth: boolean;
	hasSSO: boolean;
	ssoProviderName?: string;
}

export interface UserRelationshipUsers {
	parents: User[];
	children: User[];
	userGroupNames: { [key: string]: string[] };
}

export interface UserAttendanceStats {
	attendancePercentage: number;
	eventAttendanceCount: number;
	matchCount: number;
	practiceCount: number;
	totalEventCount: number;
}
